import React, { useMemo, useEffect, useState, useCallback } from "react";
import { Button, Form, Icon, Label, Modal } from "semantic-ui-react";
import HotelsDropDown from "../../components/shared/HotelsDropDown";
import { useTextResources } from "../../language";
import { onSuccess, onError } from "../../components/shared/messages";
import { handleStartEndChange, toServerDateFormat } from "../../components/shared/utils";
import { DatePicker } from "../../components/shared/DatePicker";
import CurrencyInput from "../../components/shared/CurrencyInput";
import WeekDaysSelect from "../../components/shared/WeekDaysSelect";
import { dateFormat } from "../../components/shared/constants";
import moment from "moment";
import { LRV, Hotels } from "../../api/services/next";
import RoomClassesDropDown from "../../components/shared/RoomClassesDropDown";
import { Currencies } from "../../components/shared/currencies";

/**
 * Bulk Update LRV Popup Component
 * Same UI pattern as OverbookingCreationPopup
 */
const BulkUpdateLrvPopup = ({ 
    open, 
    onCancelChanges,
    extendedProps: { defaultHotelId, onSave } = {}
}) => {
    const text = useTextResources();

    // State - same pattern as OverbookingCreationPopup
    const [startDate, setStartDate] = useState(toServerDateFormat(moment()));
    const [endDate, setEndDate] = useState(toServerDateFormat(moment()));
    const [selectedDates, setSelectedDates] = useState([]);
    const [days, setDays] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    
    // LRV specific state
    const [selectedHotel, setSelectedHotel] = useState(null);
    const [selectedRoomClasses, setSelectedRoomClasses] = useState([]);
    const [hurdleValue, setHurdleValue] = useState(0);
    const [hotels, setHotels] = useState([]);

    // Fetch hotels data when popup opens
    useEffect(() => {
        if (open) {
            (async () => {
                try {
                    const hotelsData = await Hotels.GetHotels();
                    setHotels(hotelsData);
                } catch (error) {
                    console.error('Error fetching hotels:', error);
                }
            })();
        }
    }, [open]);

    // Memoized selected hotel object
    const selectedHotelObj = useMemo(() => {
        if (!selectedHotel || !hotels.length) return null;
        return hotels.find(h => h.Id === selectedHotel) || null;
    }, [selectedHotel, hotels]);

    // Get currency symbol for selected hotel
    const currencySymbol = useMemo(() => {
        if (!selectedHotelObj) return '';
        return selectedHotelObj.MainCurrencyId ? Currencies[selectedHotelObj.MainCurrencyId]?.symbol || '' : '';
    }, [selectedHotelObj]);

    // Get currency ID for selected hotel
    const getCurrencyId = useCallback(() => {
        if (!selectedHotelObj) return null;
        return selectedHotelObj.MainCurrencyId || null;
    }, [selectedHotelObj]);

    // Reset state when modal closes
    useEffect(() => {
        if (!open) {
            setStartDate(toServerDateFormat(moment()));
            setEndDate(toServerDateFormat(moment()));
            setSelectedDates([]);
            setDays([]);
            setIsLoading(false);
            setSelectedHotel(null);
            setSelectedRoomClasses([]);
            setHurdleValue(0);
            setHotels([]);
        }
    }, [open]);

    // Initialize hotel with default when popup opens
    useEffect(() => {
        if (open && defaultHotelId) {
            setSelectedHotel(defaultHotelId);
        }
    }, [open, defaultHotelId]);

    // Memo for dates to pass to WeekDaysSelect - same as Overbooking
    const memoDates = useMemo(() => {
        return selectedDates.length ? selectedDates : [{ startDate, endDate }]
    }, [startDate, endDate, selectedDates]);

    // Add date to list - same as Overbooking
    const addDateToList = () => {
        if (selectedDates.some((d) => moment(d.startDate).isSame(startDate, 'day') && moment(d.endDate).isSame(endDate, 'day'))) {
            onError(text('WarningDuplicateDates') || 'This date range has already been added');
            return;
        }
        setSelectedDates([
            ...selectedDates,
            {
                startDate: startDate,
                endDate: endDate
            }
        ]);
    };

    // Get min date from selected dates (with defensive check)
    const getMinDate = () => {
        if (!selectedDates.length) return moment();
        return selectedDates.reduce((min, dateRange) => {
            const date = moment(dateRange.startDate);
            return date.isBefore(min) ? date : min;
        }, moment(selectedDates[0].startDate));
    };

    // Get max date from selected dates (with defensive check)
    const getMaxDate = () => {
        if (!selectedDates.length) return moment();
        return selectedDates.reduce((max, dateRange) => {
            const date = moment(dateRange.endDate);
            return date.isAfter(max) ? date : max;
        }, moment(selectedDates[0].endDate));
    };

    // Validation
    const getMissingFields = useCallback(() => {
        const missingFields = [];
        if (!selectedHotel) missingFields.push(text("Hotel"));
        if (selectedDates.length === 0) missingFields.push(text("Dates"));
        if (!selectedRoomClasses || selectedRoomClasses.length === 0) missingFields.push(text("RoomClass"));
        if (!days || days.length === 0) missingFields.push(text("Weekdays"));
        if (hurdleValue === null || hurdleValue === undefined || hurdleValue < 0) missingFields.push(text("Value"));
        return missingFields;
    }, [selectedHotel, selectedDates, selectedRoomClasses, days, hurdleValue, text]);

    // Save handler - same pattern as Overbooking
    const handleSave = useCallback((isClose) => async () => {
        const missingFields = getMissingFields();
        if (missingFields.length !== 0) {
            onError(text("MissingFields")?.replace('{0}', missingFields.join(", ")) || `Missing fields: ${missingFields.join(", ")}`);
            return;
        }

        try {
            setIsLoading(true);

            // Build request - filter dates by selected days
            const request = {
                hotelId: selectedHotel,
                fromDate: moment(getMinDate()).format('YYYY-MM-DD'),
                toDate: moment(getMaxDate()).format('YYYY-MM-DD'),
                daysOfWeek: days,
                currencyId: getCurrencyId(),
                roomClasses: selectedRoomClasses.map(rcId => ({
                    roomClassId: rcId,
                    hurdleValue: hurdleValue
                }))
            };

            await LRV.BulkUpdateLrv(request);

            onSuccess();
            
            // Trigger parent refresh
            if (onSave) {
                onSave();
            }

            if (isClose) {
                onCancelChanges?.();
            }
        } catch (error) {
            console.error('Bulk LRV update error:', error);
            const errorMessage = error.response?.data?.message ||
                                error.response?.data?.errors?.[0] ||
                                error.message ||
                                'An error occurred during bulk update';
            onError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    }, [selectedHotel, selectedDates, days, selectedRoomClasses, hurdleValue, onSave, onCancelChanges, text, getMissingFields]);

    // Close handler
    const handleClose = () => {
        onCancelChanges?.();
    };

    return (
        <Modal size="small" open={open} onClose={handleClose}>
            <Modal.Header style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                {`${text("Update")} ${text("LRV")}`}
            </Modal.Header>
            <Modal.Content>
                <Form>
                    {/* Date Range Chips - same as Overbooking */}
                    <Form.Group unstackable widths={'equal'} style={{ flexWrap: "wrap" }}>
                        {selectedDates.map((selectedDate, index) => (
                            <Label.Group key={index}>
                                <Label as="a">
                                    {moment(selectedDate.startDate).format(dateFormat)}
                                    -
                                    {moment(selectedDate.endDate).format(dateFormat)}
                                    <Icon key={index} name={"delete"} onClick={() => setSelectedDates(selectedDates.filter((dateRange, i) => i !== index))} />
                                </Label>
                            </Label.Group>
                        ))}
                    </Form.Group>

                    {/* Date Pickers - same as Overbooking */}
                    <Form.Group widths="equal">
                        <DatePicker
                            key="fromDate"
                            label={text("From")}
                            onChange={(value) => handleStartEndChange([value, endDate], 0, setStartDate, setEndDate)}
                            clearable={false}
                            value={startDate}
                        />
                        <DatePicker
                            key="toDate"
                            minDate={startDate}
                            label={text("To")}
                            onChange={(value) => handleStartEndChange([startDate, value], 1, setStartDate, setEndDate)}
                            clearable={false}
                            value={endDate}
                        />
                    </Form.Group>

                    {/* WeekDaysSelect and Add Button - same as Overbooking */}
                    <Form.Group style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between" }}>
                        <Form.Field label={text("Weekdays")} control={WeekDaysSelect} onChange={setDays} dates={memoDates} />
                        <div className="flex-center-center">
                            <Button primary onClick={addDateToList}>{text("Add")}</Button>
                        </div>
                    </Form.Group>

                    {/* Hotel Selection */}
                    <Form.Field
                        fluid
                        variant="outlined"
                        label={text("Hotel")}
                        required
                        control={HotelsDropDown}
                        onChange={setSelectedHotel}
                        value={selectedHotel}
                        search
                    />

                    {/* Room Class Selection */}
                    <Form.Field
                        label={text("RoomClass")}
                        placeholder={text("Select")}
                        control={RoomClassesDropDown}
                        hotelId={selectedHotel}
                        value={selectedRoomClasses}
                        multiple
                        clearable
                        onChange={setSelectedRoomClasses}
                        disabled={!selectedHotel}
                        search
                    />

                    {/* Hurdle Value */}
                    <Form.Field
                        label={text("Value")}
                        control={CurrencyInput}
                        digits={2}
                        min={0}
                        max={999999}
                        onChange={setHurdleValue}
                        value={hurdleValue}
                        currency={currencySymbol}
                    />
                </Form>
            </Modal.Content>
            <Modal.Actions style={{ display: "flex", justifyContent: "flex-end" }}>
                <Button negative onClick={handleClose}>
                    {text('Close')}
                </Button>
                <Button positive onClick={handleSave(true)} loading={isLoading}>
                    {text("Saveandclose")}
                </Button>
                <Button positive onClick={handleSave(false)} loading={isLoading}>
                    {text("Save")}
                </Button>
            </Modal.Actions>
        </Modal>
    );
};

export default BulkUpdateLrvPopup;
